import React, { useState } from 'react';
import { Button, ListGroup, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EventModalMediaContent from '../../../../../components/EventModalMediaContent';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import Flex from '../../../../../components/common/Flex';

// 获取文件图标
const getFileIcon = (type) => {
	if (type?.includes('image')) {
		return ['far', 'file-image'];
	}
	if (type?.includes('video')) {
		return ['far', 'file-video'];
	}
	if (type?.includes('audio')) {
		return ['far', 'file-audio'];
	}
	if (type?.includes('zip') || type?.includes('archive')) {
		return ['far', 'file-archive'];
	}
	if (type?.includes('pdf')) {
		return ['far', 'file-pdf'];
	}
	if (type?.includes('word') || type?.includes('document')) {
		return ['far', 'file-word'];
	}
	if (type?.includes('excel') || type?.includes('spreadsheet')) {
		return ['far', 'file-excel'];
	}
	return ['far', 'file'];
};

// 格式化文件名
const formatFileName = (name, maxLength = 30) => {
	if (!name) return '';
	if (name.length <= maxLength) return name;
	const [fileName, extension] = name.split('.');
	if (extension) {
		return `${fileName.slice(0, maxLength - extension.length - 3)}...${extension}`;
	}
	return `${name.slice(0, maxLength - 3)}...`;
};

// 格式化文件大小
const formatFileSize = (size) => {
	if (!size) return '0 KB';
	const sizeNum = typeof size === 'string' ? parseFloat(size) : size;
	if (sizeNum < 1024) {
		return `${sizeNum} B`;
	} else if (sizeNum < 1024 * 1024) {
		return `${(sizeNum / 1024).toFixed(2)} KB`;
	} else {
		return `${(sizeNum / (1024 * 1024)).toFixed(2)} MB`;
	}
};

// 判断是否为图片文件
const isImageFile = (type) => {
	return type?.includes('image');
};

const OrderAttachmentList = () => {
	const { order } = useOrderContext();
	const attachments = Array.isArray(order?.attachments) ? order.attachments : [];

	// 跟踪正在下载的文件ID
	const [downloadingFileId, setDownloadingFileId] = useState(null);

	// 构建文件下载URL - 使用 API 端点
	const getFileUrl = (attachment) => {
		// 优先使用 filename（这是服务器上的实际文件名）
		if (attachment.filename) {
			return `/api/upload/order/attachment/${encodeURIComponent(attachment.filename)}`;
		}

		// 其次使用 id（通常 id 就是 filename）
		if (attachment.id) {
			return `/api/upload/order/attachment/${encodeURIComponent(attachment.id)}`;
		}

		// 如果 path 存在，尝试从 path 中提取文件名
		if (attachment.path) {
			// 从路径中提取文件名
			const pathParts = attachment.path.split('/');
			const filename = pathParts[pathParts.length - 1];
			if (filename) {
				return `/api/upload/order/attachment/${encodeURIComponent(filename)}`;
			}
		}

		return '';
	};

	// 处理文件下载
	const handleDownload = async (attachment) => {
		const fileUrl = getFileUrl(attachment);
		const fileId = attachment.id || attachment.filename;

		if (!fileUrl) {
			toast.error('无法获取文件URL');
			return;
		}

		// 设置下载状态
		setDownloadingFileId(fileId);

		try {
			// 使用fetch下载文件，这样可以携带cookie认证信息
			const response = await fetch(fileUrl, {
				method: 'GET',
				credentials: 'include', // 包含cookie
				mode: 'cors', // 明确指定 CORS 模式
				headers: {
					// 确保请求头正确
					Accept: '*/*',
				},
			}).catch((fetchError) => {
				// 捕获网络错误
				throw new Error(`网络请求失败: ${fetchError.message || '无法连接到服务器'}`);
			});

			if (response.status === 401) {
				toast.error('未授权，请重新登录');
				setDownloadingFileId(null);
				return;
			}

			if (response.status === 404) {
				toast.error('文件不存在');
				setDownloadingFileId(null);
				return;
			}

			if (!response.ok) {
				const errorText = await response.text();
				const errorMessage = errorText ? `${response.status} ${response.statusText}: ${errorText}` : `${response.status} ${response.statusText}`;
				throw new Error(`下载失败: ${errorMessage}`);
			}

			// 获取文件blob
			const blob = await response.blob();

			if (!blob || blob.size === 0) {
				throw new Error('文件内容为空');
			}

			// 创建blob URL
			const blobUrl = window.URL.createObjectURL(blob);

			// 创建临时链接下载文件
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = attachment.name || attachment.filename || 'file';
			document.body.appendChild(link);
			link.click();

			// 清理
			document.body.removeChild(link);
			window.URL.revokeObjectURL(blobUrl); // 释放blob URL

			toast.success('文件下载成功');
		} catch (error) {
			toast.error(`文件下载失败: ${error.message || '请稍后重试'}`);
		} finally {
			// 清除下载状态
			setDownloadingFileId(null);
		}
	};

	// 处理文件查看（在新标签页打开）
	const handleView = (attachment) => {
		const fileUrl = getFileUrl(attachment);
		if (!fileUrl) {
			return;
		}
		window.open(fileUrl, '_blank');
	};

	// 如果没有附件，不显示任何内容
	if (!attachments || attachments.length === 0) {
		return null;
	}

	return (
		<EventModalMediaContent icon='paperclip' heading='附件'>
			<ListGroup variant='flush'>
				{attachments.map((attachment, index) => {
					const fileIcon = getFileIcon(attachment.type);
					const fileUrl = getFileUrl(attachment);
					const isImage = isImageFile(attachment.type);
					const fileId = attachment.id || attachment.filename;
					const isDownloading = downloadingFileId === fileId;

					return (
						<ListGroup.Item key={attachment.id || index} className='border rounded mb-2'>
							<Flex alignItems='center' className='gap-2'>
								<FontAwesomeIcon icon={fileIcon} className='fs-5 text-primary' />
								<div className='flex-1' style={{ minWidth: 0 }}>
									<div className='fw-semibold text-truncate' title={attachment.name || attachment.filename || '未知文件'}>
										{formatFileName(attachment.name || attachment.filename || '未知文件')}
									</div>
									<div className='text-muted small'>{formatFileSize(attachment.size)}</div>
								</div>
								<div className='d-flex gap-1'>
									{isImage && fileUrl && !isDownloading && (
										<OverlayTrigger
											overlay={
												<Tooltip style={{ position: 'fixed' }} id={`viewTooltip${index}`}>
													查看图片
												</Tooltip>
											}
										>
											<Button variant='link' size='sm' className='p-1' onClick={() => handleView(attachment)}>
												<FontAwesomeIcon icon='eye' className='text-primary' />
											</Button>
										</OverlayTrigger>
									)}
									<OverlayTrigger
										overlay={
											<Tooltip style={{ position: 'fixed' }} id={`downloadTooltip${index}`}>
												{isDownloading ? '正在下载...' : '下载附件'}
											</Tooltip>
										}
									>
										<Button variant='link' size='sm' className='p-1' onClick={() => handleDownload(attachment)} disabled={!fileUrl || isDownloading}>
											{isDownloading ? <Spinner size='sm' variant='success' animation='border' /> : <FontAwesomeIcon icon='download' className='text-success' />}
										</Button>
									</OverlayTrigger>
								</div>
							</Flex>
						</ListGroup.Item>
					);
				})}
			</ListGroup>
		</EventModalMediaContent>
	);
};

export default OrderAttachmentList;
